import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from './api';
import { EveningCheckOut, MOODS_BEFORE, MorningCheckIn, TRADING_EMOTIONS, readiness } from './Emotions';
import { goalProgress } from './Goals';
import { noteType } from './sessionNotes';
import { dayKey, formatMoney, netPnl, tradingDayKey } from './pnl';
import { currentLocale, translateNow } from './langState';
import { useI18n } from './i18n';
import { TodayEvents } from './MarketCalendar';
import { ExperimentCheck, ExperimentLine } from './ExperimentPanel';
import { BRIEF_URL } from './config';
import { TradeIdeasPlan, TradeIdeasReview } from './TradeIdeas';
import Moments from './Moments';

const EMPTY_DAY = {
  plan: '', review: '', morningDone: [], eveningDone: [], rulesFollowed: [], score: null, workOn: '',
  moodBefore: [], sleepHours: null, rested: null, energy: null, stress: null,
  moodAfter: [], emotionImpact: null, energyAfter: null, emotionsHit: [], emotionTrigger: '',
  sessionLog: [], sessionTrades: [], focus: '', focusFollowed: null, checkInOk: false, experimentFollowed: null,
  ideas: [], moments: [],
};
const OPTIONAL_FIELDS = ['score', 'sleepHours', 'rested', 'energy', 'stress', 'emotionImpact', 'energyAfter'];

// True when a day note has anything worth keeping.
export function noteHasContent(n) {
  return Boolean(
    n && (n.plan || n.review || n.workOn || n.focus || n.focusFollowed || n.experimentFollowed || n.emotionTrigger || OPTIONAL_FIELDS.some((k) => n[k] != null) ||
      ['morningDone', 'eveningDone', 'rulesFollowed', 'moodBefore', 'moodAfter', 'emotionsHit', 'sessionLog', 'sessionTrades', 'ideas', 'moments']
        .some((k) => n[k] && n[k].length))
  );
}

function shiftDay(key, n) {
  const d = new Date(`${key}T12:00:00`);
  d.setDate(d.getDate() + n);
  return dayKey(d);
}

function dayFromNote(note) {
  const day = { ...EMPTY_DAY, ...(note || {}) };
  // The server leaves out unset values; use null so inputs know they're empty.
  for (const key of OPTIONAL_FIELDS) if (day[key] === undefined) day[key] = null;
  return day;
}

function Checklist({ items, done, onToggle, emptyText }) {
  if (!items.length) return <p className="sidebar-muted">{emptyText}</p>;
  return (
    <ul className="checklist">
      {items.map((item) => {
        const checked = done.includes(item);
        return (
          <li key={item}>
            <label className={`check-item${checked ? ' checked' : ''}`}>
              <input type="checkbox" checked={checked} onChange={() => onToggle(item)} />
              <span>{item}</span>
            </label>
          </li>
        );
      })}
    </ul>
  );
}

function Progress({ done, total }) {
  if (!total) return null;
  return (
    <span className={`routine-progress${done === total ? ' complete' : ''}`}>
      {done}/{total}
    </span>
  );
}

// Editor for one ordered list (rules or a checklist): add, remove, reorder.
export function ListEditor({ label, items, onChange, numbered, placeholder }) {
  const { t } = useI18n();
  const [draft, setDraft] = useState('');

  function move(i, by) {
    const next = [...items];
    const [item] = next.splice(i, 1);
    next.splice(i + by, 0, item);
    onChange(next);
  }

  function add() {
    const text = draft.trim();
    if (!text || items.includes(text)) return;
    onChange([...items, text]);
    setDraft('');
  }

  return (
    <div className="list-editor">
      <span className="modal-section-label">{label}</span>
      <ol className="list-editor-items">
        {items.map((item, i) => (
          // Index keys keep the cursor in place while an item is being edited.
          // eslint-disable-next-line react/no-array-index-key
          <li key={i}>
            {numbered && <span className="rule-number">{t('Rule {n}', { n: i + 1 })}</span>}
            <input
              value={item}
              maxLength={200}
              onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))}
              aria-label={t('{label} item {n}', { label, n: i + 1 })}
            />
            <button type="button" className="icon-button" onClick={() => move(i, -1)} disabled={i === 0} aria-label={t('Move up')}>&uarr;</button>
            <button type="button" className="icon-button" onClick={() => move(i, 1)} disabled={i === items.length - 1} aria-label={t('Move down')}>&darr;</button>
            <button type="button" className="icon-button remove" onClick={() => onChange(items.filter((_, j) => j !== i))} aria-label={t('Remove')}>&times;</button>
          </li>
        ))}
      </ol>
      <div className="tag-create">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          placeholder={placeholder}
          maxLength={200}
        />
        <button type="button" className="secondary small" onClick={add} disabled={!draft.trim()}>{t('Add')}</button>
      </div>
    </div>
  );
}

function RoutineEditor({ routine, onSaved, onClose, onSessionExpired }) {
  const { t } = useI18n();
  const [form, setForm] = useState(routine);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const dialog = useRef(null);

  useEffect(() => {
    dialog.current?.focus();
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function save() {
    setSaving(true);
    setError('');
    try {
      const clean = (list) => list.map((x) => x.trim()).filter(Boolean);
      const saved = await api('/routine', {
        method: 'PUT',
        body: { rules: clean(form.rules), morning: clean(form.morning), evening: clean(form.evening) },
      });
      onSaved(saved);
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={dialog} tabIndex={-1} className="modal modal-wide" role="dialog" aria-modal="true" aria-labelledby="routine-edit-title">
        <div className="modal-header">
          <h2 id="routine-edit-title">{t('Rules & checklists')}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label={t('Close')}>&times;</button>
        </div>
        <p className="hint">
          {t('Put your most important rule first. Use the arrows to change the order. Ticks on past days are kept even if you reword an item later.')}
        </p>
        <ListEditor label={t('My trading rules')} numbered items={form.rules}
          onChange={(rules) => setForm({ ...form, rules })} placeholder={t('Add a rule, e.g. No trades in the first 5 minutes')} />
        <ListEditor label={t('Morning ritual (before the open)')} items={form.morning}
          onChange={(morning) => setForm({ ...form, morning })} placeholder={t('Add a morning step')} />
        <ListEditor label={t('After the close')} items={form.evening}
          onChange={(evening) => setForm({ ...form, evening })} placeholder={t('Add an after-close step')} />
        {error && <p className="error">{error}</p>}
        <div className="modal-actions">
          <button type="button" onClick={save} disabled={saving}>{saving ? t('Saving…') : t('Save')}</button>
          <button type="button" className="secondary" onClick={onClose} disabled={saving}>{t('Cancel')}</button>
        </div>
      </div>
    </div>
  );
}

// Which routine page fits the time of day: Morning prep until the US market
// closes (4 PM New York time), Reflection after.
export function routinePageForNow() {
  const hour = Number(new Date().toLocaleString('en-US', { timeZone: 'America/New_York', hour: 'numeric', hourCycle: 'h23' }));
  return hour >= 16 ? 'reflection' : 'morning';
}

export function listWords(words) {
  return words.length <= 1 ? words.join('') : `${words.slice(0, -1).join(', ')} ${translateNow('and')} ${words[words.length - 1]}`;
}

// What carries over from the last session: the emotions that took over (with a
// reminder for each), what triggered them, and what the trader said they'd work on.
// A one-line version of "what I'll work on": the first sentence, cut at a word
// near 90 characters. Only ever a shortening of the trader's own words.
export function shortFocus(text) {
  const clean = String(text || '').trim().replace(/\s+/g, ' ');
  if (!clean) return '';
  const first = clean.split(/(?<=[.!?])\s/)[0];
  if (first.length <= 90) return first;
  const cut = first.slice(0, 90);
  return `${cut.slice(0, cut.lastIndexOf(' ') > 40 ? cut.lastIndexOf(' ') : 90).replace(/[,;:]$/, '')}…`;
}

// The most recent earlier day with a focus to carry forward.
function lastFocusDay(notes, date) {
  return Object.keys(notes).filter((k) => k < date && (notes[k].focus || notes[k].workOn)).sort().pop() || null;
}

// On the reflection: did you follow the focus you set for today?
export function FocusCheck({ notes, date, day, update }) {
  const { t } = useI18n();
  const key = lastFocusDay(notes, date);
  if (!key) return null;
  const focus = notes[key].focus || shortFocus(notes[key].workOn);
  const options = [['yes', 'Yes'], ['partly', 'Partly'], ['no', 'No']];
  return (
    <section className="focus-check">
      <span className="focus-today-label">{t('Today’s focus')}</span>
      <p className="focus-line">{focus}</p>
      <div className="focus-check-row" role="group" aria-label={t('Did you follow it?')}>
        <span>{t('Did you follow it?')}</span>
        {options.map(([id, label]) => (
          <button key={id} type="button" className={`secondary small${day.focusFollowed === id ? ' active' : ''}`} aria-pressed={day.focusFollowed === id}
            onClick={() => update({ focusFollowed: day.focusFollowed === id ? null : id })}>
            {t(label)}
          </button>
        ))}
      </div>
    </section>
  );
}

// Ticks on Home's focus checklist, kept on this device for the day.
const FOCUS_CHECKS_KEY = 'mm-focus-checks';
function readChecks(date) {
  try {
    const saved = JSON.parse(localStorage.getItem(FOCUS_CHECKS_KEY)) || {};
    return saved.date === date ? saved.done || [] : [];
  } catch {
    return [];
  }
}

// With `checklist` (Home), the focus becomes a short list to tick off during the day, and the
// emotion details fold under "Read your full note".
export function FocusToday({ notes, date, checklist = false }) {
  const { t } = useI18n();
  const [done, setDone] = useState(() => readChecks(date));
  const lastKey = Object.keys(notes)
    .filter((k) => k < date && ((notes[k].emotionsHit && notes[k].emotionsHit.length) || notes[k].workOn || notes[k].focus))
    .sort()
    .pop();
  if (!lastKey) return null;
  const last = notes[lastKey];
  const hit = TRADING_EMOTIONS.filter((e) => (last.emotionsHit || []).includes(e.id));
  const when = new Date(`${lastKey}T12:00:00`).toLocaleDateString(currentLocale(), { weekday: 'long' });

  if (checklist) {
    const line = last.focus || (last.workOn ? shortFocus(last.workOn) : '');
    const items = [
      ...(line ? [t('Stick to my focus')] : []),
      ...(hit.length ? [t('Keep {emotions} in check', { emotions: listWords(hit.map((e) => t(e.id))) })] : []),
      t('Follow my plan'),
    ];
    const toggle = (item) => {
      const next = done.includes(item) ? done.filter((x) => x !== item) : [...done, item];
      setDone(next);
      try {
        localStorage.setItem(FOCUS_CHECKS_KEY, JSON.stringify({ date, done: next }));
      } catch {
        // Not remembered in private mode.
      }
    };
    return (
      <section className={`focus-today focus-checklist${hit.length ? ' has-warnings' : ''}`}>
        <div className="focus-today-head">
          <span className="focus-today-label">{t('Your focus today')}</span>
          <span className="focus-today-from">{t('From your {day} reflection', { day: when })}</span>
        </div>
        {line && <p className="focus-checklist-line">{line}</p>}
        <ul className="focus-checks">
          {items.map((item) => (
            <li key={item}>
              <label className={done.includes(item) ? 'is-done' : ''}>
                <input type="checkbox" checked={done.includes(item)} onChange={() => toggle(item)} />
                <span>{item}</span>
              </label>
            </li>
          ))}
        </ul>
        {(hit.length > 0 || (last.workOn && last.workOn.trim() !== line)) && (
          <details className="focus-full">
            <summary>{t('Read your full note')}</summary>
            {hit.map((e) => (
              <p key={e.id}><strong><span aria-hidden="true">{e.face}</span> {t(e.id)}:</strong> {t(e.tip)}</p>
            ))}
            {last.emotionTrigger && <p><strong>{t('What triggered it')}:</strong> {last.emotionTrigger}</p>}
            {last.workOn && last.workOn.trim() !== line && <p>{last.workOn}</p>}
          </details>
        )}
      </section>
    );
  }

  return (
    <section className={`focus-today${hit.length ? ' has-warnings' : ''}`}>
      <div className="focus-today-head">
        <span className="focus-today-label">{t('Your focus today')}</span>
        <span className="focus-today-from">{t('From your {day} reflection', { day: when })}</span>
      </div>
      {hit.length > 0 && (
        <>
          <p className="focus-today-lead">
            {t(hit.length === 1 ? 'On {day}, {emotions} got the better of you. Watch for it today.' : 'On {day}, {emotions} got the better of you. Watch for them today.',
              { day: when, emotions: listWords(hit.map((e) => t(e.id))) })}
          </p>
          <ul className="focus-warnings">
            {hit.map((e) => (
              <li key={e.id}>
                <span className="focus-warning-name"><span aria-hidden="true">{e.face}</span> {t(e.id)}</span>
                <span className="focus-warning-tip">{t(e.tip)}</span>
              </li>
            ))}
          </ul>
          {last.emotionTrigger && (
            <p className="focus-trigger"><span className="scale-label">{t('What triggered it')}</span> {last.emotionTrigger}</p>
          )}
        </>
      )}
      {(last.focus || last.workOn) && (
        <div className="focus-workon">
          <span className="scale-label">{t("What you said you'd work on")}</span>
          <p className="focus-line">{last.focus || shortFocus(last.workOn)}</p>
          {last.workOn && last.workOn.trim() !== (last.focus || shortFocus(last.workOn)) && (
            <details className="focus-full">
              <summary>{t('Read your full note')}</summary>
              <p>{last.workOn}</p>
            </details>
          )}
        </div>
      )}
    </section>
  );
}

// The notes written during the Live session, in time order, for the reflection.
function SessionTimeline({ day, onOpen }) {
  const { t } = useI18n();
  const log = day.sessionLog || [];
  const taps = day.sessionTrades || [];
  if (!log.length && !taps.length) return null;
  const wins = taps.filter((x) => x.result === 'win').length;
  return (
    <div className="routine-block session-timeline">
      <div className="routine-block-head">
        <span className="routine-block-title">{t('Your session notes')}</span>
        {onOpen && <button type="button" className="link-button" onClick={onOpen}>{t('Open live session')}</button>}
      </div>
      {taps.length > 0 && (
        <span className="timeline-taps">
          {t('Tracked {trades}: {wins}, {losses}', {
            trades: t(taps.length === 1 ? '{n} trade' : '{n} trades', { n: taps.length }),
            wins: t(wins === 1 ? '{n} win' : '{n} wins', { n: wins }),
            losses: t(taps.length - wins === 1 ? '{n} loss' : '{n} losses', { n: taps.length - wins }),
          })}
        </span>
      )}
      <ol className="session-log">
        {log.map((entry, i) => {
          const type = noteType(entry.type);
          return (
            // eslint-disable-next-line react/no-array-index-key
            <li key={i} className={`log-${entry.type}`}>
              <span className="log-time">{new Date(entry.time).toLocaleTimeString(currentLocale(), { hour: 'numeric', minute: '2-digit' })}</span>
              <span className="log-type" title={t(type.label)} aria-label={t(type.label)}>{type.icon}</span>
              <span className="log-text">{entry.text}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function MorningSummary({ day, routinePlan }) {
  const { t } = useI18n();
  const ready = readiness(day);
  const faces = MOODS_BEFORE.filter((m) => day.moodBefore.includes(m.id));
  const hasCheckIn = faces.length || day.sleepHours != null || day.rested || day.energy || day.stress;
  return (
    <div className="routine-block morning-summary">
      <div className="routine-block-head">
        <span className="routine-block-title">{t('Your morning')}</span>
        {routinePlan}
      </div>
      {hasCheckIn ? (
        <div className="morning-summary-checkin">
          {faces.length > 0 && (
            <span className="summary-faces">
              {faces.map((m) => <span key={m.id} title={t(m.id)}>{m.face} {t(m.id)}</span>)}
            </span>
          )}
          <span className="summary-stats">
            {day.sleepHours != null && <span>{t('{n}h sleep', { n: day.sleepHours })}</span>}
            {day.rested && <span>{t('Rested {n}/5', { n: day.rested })}</span>}
            {day.energy && <span>{t('Energy {n}/5', { n: day.energy })}</span>}
            {day.stress && <span>{t('Stress {n}/5', { n: day.stress })}</span>}
          </span>
          {ready && <span className={`summary-ready readiness-text-${ready.level}`}>{t(ready.title)}</span>}
        </div>
      ) : (
        <p className="sidebar-muted">{t('No morning check-in for this day.')}</p>
      )}
      <div className="morning-summary-plan">
        <span className="scale-label">{t('Plan')}</span>
        {day.plan ? <p>{day.plan}</p> : <p className="sidebar-muted">{t('No plan written.')}</p>}
      </div>
    </div>
  );
}

// Morning reminder of the bigger picture: the trader's vision and the short-term
// goal that's due soonest.
function WhyStrip({ goals, vision, data, onOpen }) {
  const { t } = useI18n();
  const active = (goals || [])
    .filter((g) => g.horizon === 'short' && !goalProgress(g, data).done)
    .sort((a, b) => (a.targetDate || '9999').localeCompare(b.targetDate || '9999'));
  const top = active[0];
  if (!vision && !top) return null;
  const p = top ? goalProgress(top, data) : null;
  return (
    <button type="button" className="why-strip" onClick={onOpen}>
      {vision && (
        <span className="why-item">
          <span className="why-label">{t('My vision')}</span>
          <span className="why-text">{vision.split('\n')[0]}</span>
        </span>
      )}
      {top && (
        <span className="why-item">
          <span className="why-label">{t('Current goal')}</span>
          <span className="why-text">{top.title}</span>
          <span className="why-progress"><span style={{ width: `${p.progress * 100}%` }} /></span>
        </span>
      )}
    </button>
  );
}

function Routine({ mode, trades, accounts = [], allTrades = [], notes, setNotes, experiments = [], onSessionExpired }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const today = dayKey(new Date());
  const requested = searchParams.get('date');
  const [date, setDate] = useState(() => (requested && /^\d{4}-\d{2}-\d{2}$/.test(requested) && requested <= today ? requested : today));
  const [routine, setRoutine] = useState(null);
  const [goalsData, setGoalsData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [day, setDay] = useState(() => dayFromNote(notes[date]));
  const [saveState, setSaveState] = useState('idle');
  const pending = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    api('/routine')
      .then(setRoutine)
      .catch((err) => {
        if (err.status === 401) onSessionExpired();
      });
  }, [onSessionExpired]);

  useEffect(() => {
    if (mode !== 'morning') return;
    api('/goals').then(setGoalsData).catch(() => {});
  }, [mode]);

  // Save any pending changes before switching days or leaving the page.
  function flush() {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    if (pending.current) {
      const { key, changes } = pending.current;
      pending.current = null;
      persist(key, changes);
    }
  }

  useEffect(() => () => flush(), []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setDay(dayFromNote(notes[date]));
    setSaveState('idle');
  }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  // Saved notes load after the page opens (and change when the calendar saves);
  // show them unless the trader has unsaved edits for this day.
  const savedNote = notes[date];
  // Text the server only trimmed (a space typed just before the save) stays as typed, so the
  // cursor's space isn't taken away mid-sentence.
  useEffect(() => {
    if (pending.current && pending.current.key === date) return;
    setDay((current) => {
      const next = dayFromNote(savedNote);
      for (const key of Object.keys(next)) {
        if (typeof next[key] === 'string' && typeof current[key] === 'string' && current[key].trim() === next[key].trim()) next[key] = current[key];
      }
      return next;
    });
  }, [savedNote]); // eslint-disable-line react-hooks/exhaustive-deps

  async function persist(key, changes) {
    setSaveState('saving');
    try {
      const saved = await api(`/notes/${key}`, { method: 'PUT', body: changes });
      setNotes((prev) => {
        const next = { ...prev };
        if (noteHasContent(saved)) next[key] = saved;
        else delete next[key];
        return next;
      });
      setSaveState('saved');
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setSaveState('error');
    }
  }

  // Updates the day and saves shortly after the trader stops typing or ticking.
  function update(changes) {
    setDay((d) => ({ ...d, ...changes }));
    const merged = pending.current && pending.current.key === date ? { ...pending.current.changes, ...changes } : changes;
    pending.current = { key: date, changes: merged };
    setSaveState('pending');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(flush, 700);
  }

  function goTo(key) {
    flush();
    setDate(key);
  }

  // Switch to the other routine page (or the journal) for the same day.
  function openPage(path) {
    flush();
    navigate(date === today ? path : `${path}?date=${date}`);
  }

  function toggle(field, item) {
    const list = day[field];
    update({ [field]: list.includes(item) ? list.filter((x) => x !== item) : [...list, item] });
  }

  const dayTrades = trades.filter((x) => tradingDayKey(x.date) === date);
  const dayNet = dayTrades.reduce((s, x) => s + netPnl(x), 0);
  const dayWins = dayTrades.filter((x) => netPnl(x) > 0).length;
  const rules = routine ? routine.rules : [];
  const morning = routine ? routine.morning : [];
  const evening = routine ? routine.evening : [];
  const rulesKept = rules.filter((r) => day.rulesFollowed.includes(r)).length;
  const dateLabel = new Date(`${date}T12:00:00`).toLocaleDateString(currentLocale(), { weekday: 'long', month: 'long', day: 'numeric' });
  const isMorning = mode === 'morning';

  return (
    <div className={`routine routine-${mode}`}>
      <div className="routine-header routine-header-big">
        <div>
          <p className="eyebrow">{isMorning ? t('Morning prep') : t('End-of-day reflection')}</p>
          <h1 className="routine-title">{date === today ? t('Today') : dateLabel}</h1>
          {date === today && <p className="subtitle">{dateLabel}</p>}
        </div>
        <div className="routine-header-actions">
          <span className={`save-status ${saveState === 'saved' ? 'success' : saveState === 'error' ? 'error' : ''}`}>
            {saveState === 'saving' || saveState === 'pending' ? t('Saving…') : saveState === 'saved' ? t('Saved') : saveState === 'error' ? t('Could not save') : ''}
          </span>
          <div className="calendar-nav">
            <button type="button" className="secondary small" onClick={() => goTo(shiftDay(date, -1))} aria-label={t('Previous day')}>&larr;</button>
            <button type="button" className="secondary small" onClick={() => goTo(today)} disabled={date === today}>{t('Today')}</button>
            <button type="button" className="secondary small" onClick={() => goTo(shiftDay(date, 1))} disabled={date >= today} aria-label={t('Next day')}>&rarr;</button>
          </div>
        </div>
      </div>

      {isMorning && goalsData && (
        <WhyStrip goals={goalsData.goals} vision={goalsData.vision}
          data={{ accounts, trades: allTrades, notes, rules: routine ? routine.rules : [] }}
          onOpen={() => openPage('/app/goals')} />
      )}
      {isMorning && <ExperimentLine experiments={experiments} />}
      {isMorning && <FocusToday notes={notes} date={date} />}
      {isMorning && date === today && <TodayEvents />}
      {!isMorning && <FocusCheck notes={notes} date={date} day={day} update={update} />}
      {!isMorning && date === today && <ExperimentCheck experiments={experiments} day={day} update={update} />}

      {isMorning ? (
        <div className="routine-grid">
          <div className="routine-col">
            <MorningCheckIn day={day} update={update} />

            <TradeIdeasPlan ideas={day.ideas} onChange={(ideas) => update({ ideas })} />
          </div>
          <div className="routine-col">
            <div className="routine-block rules-block">
              <div className="routine-block-head">
                <span className="routine-block-title">{t('My rules')}</span>
                <button type="button" className="link-button" onClick={() => setEditing(true)}>{t('Edit rules & checklists')}</button>
              </div>
              {rules.length ? (
                <ol className="rules-list">
                  {rules.map((rule, i) => (
                    <li key={rule}>
                      <span className="rule-number">{t('Rule {n}', { n: i + 1 })}</span>
                      <span className="rule-text">{rule}</span>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="sidebar-muted">{t('Add the rules you want to see every morning.')}</p>
              )}
            </div>

            <div className="routine-block">
              <div className="routine-block-head">
                <span className="routine-block-title">{t('Morning ritual')}</span>
                <Progress done={morning.filter((m) => day.morningDone.includes(m)).length} total={morning.length} />
              </div>
              <Checklist items={morning} done={day.morningDone} onToggle={(item) => toggle('morningDone', item)}
                emptyText={t('Add the steps you do every morning before the open.')} />
              {BRIEF_URL && (
                <a href={BRIEF_URL} target="_blank" rel="noopener noreferrer" className="routine-brief-link">
                  {t("Read today's pre-market brief on Discord")} &rarr;
                </a>
              )}
            </div>

            <div className="routine-block">
              <label className="routine-field">
                <span className="routine-block-title">{t("Today's plan")}</span>
                <textarea rows="5" value={day.plan} onChange={(e) => update({ plan: e.target.value })}
                  placeholder={t("Bias, key levels, the setups you'll take, and your max loss for the day…")} />
              </label>
            </div>
          </div>
        </div>
      ) : (
        <div className="routine-grid">
          <div className="routine-col">
            <MorningSummary
              day={day}
              routinePlan={
                <button type="button" className="link-button" onClick={() => openPage('/app/morning')}>{t('Open morning prep')}</button>
              }
            />

            <SessionTimeline day={day} onOpen={date === today ? () => openPage('/app/session') : null} />

            <TradeIdeasReview ideas={day.ideas} onChange={(ideas) => update({ ideas })} trades={dayTrades} />

            <div className="routine-block day-results">
              <span className="routine-block-title">{t('Results')}</span>
              {dayTrades.length ? (
                <div className="day-results-row">
                  <span className={`day-results-net ${dayNet >= 0 ? 'gain' : 'loss'}`}>{formatMoney(dayNet)}</span>
                  <span>{t(dayTrades.length === 1 ? '{n} trade' : '{n} trades', { n: dayTrades.length })}</span>
                  <span>{t('{w}W / {l}L', { w: dayWins, l: dayTrades.length - dayWins })}</span>
                </div>
              ) : (
                <p className="sidebar-muted">{t("No trades on this day yet. Import today's trades to see your results here.")}</p>
              )}
            </div>

            <EveningCheckOut day={day} update={update} />

            <Moments day={day} trades={dayTrades} update={update} />
          </div>
          <div className="routine-col">
            <div className="routine-block">
              <div className="routine-block-head">
                <span className="routine-block-title">{t('Did I follow my rules?')}</span>
                <Progress done={rulesKept} total={rules.length} />
              </div>
              <Checklist items={rules} done={day.rulesFollowed} onToggle={(item) => toggle('rulesFollowed', item)}
                emptyText={t('Add rules to check them off here.')} />
            </div>

            <div className="routine-block">
              <div className="routine-block-head">
                <span className="routine-block-title">{t('After the close')}</span>
                <Progress done={evening.filter((m) => day.eveningDone.includes(m)).length} total={evening.length} />
              </div>
              <Checklist items={evening} done={day.eveningDone} onToggle={(item) => toggle('eveningDone', item)}
                emptyText={t('Add the steps you do after the market closes.')} />
            </div>

            <div className="routine-block">
              <span className="routine-block-title">{t('Score your day')}</span>
              <div className="score-picker" role="radiogroup" aria-label={t('Score your day from 1 to 10')}>
                {[...Array(10)].map((_, i) => {
                  const n = i + 1;
                  return (
                    <button key={n} type="button" role="radio" aria-checked={day.score === n}
                      className={`score-button${day.score === n ? ' selected' : ''}${n <= 3 ? ' low' : n <= 6 ? ' mid' : ' high'}`}
                      onClick={() => update({ score: day.score === n ? null : n })}>
                      {n}
                    </button>
                  );
                })}
              </div>
              <span className="field-hint">{t('Score how well you traded your plan, not how much you made.')}</span>
            </div>

            <div className="routine-block">
              <label className="routine-field">
                <span className="routine-block-title">{t('Reflection')}</span>
                <textarea rows="4" value={day.review} onChange={(e) => update({ review: e.target.value })}
                  placeholder={t("What went well? What didn't? How did you feel?")} />
              </label>
              <label className="routine-field">
                <span className="routine-block-title">{t("What I'll work on tomorrow")}</span>
                <textarea rows="2" value={day.workOn} onChange={(e) => update({ workOn: e.target.value })}
                  placeholder={t('One thing to focus on next session')} maxLength={2000} />
              </label>
              <label className="routine-field">
                <span className="scale-label">{t('Tomorrow’s focus, in one line')}</span>
                <input type="text" value={day.focus} maxLength={120} onChange={(e) => update({ focus: e.target.value })}
                  placeholder={shortFocus(day.workOn) || t('e.g. No trades after 2 PM')} />
              </label>
              <span className="field-hint">
                {t('This short line shows on Home, Morning prep and the Live session tomorrow.')}
                {!day.focus && shortFocus(day.workOn) && (
                  <> <button type="button" className="link-button" onClick={() => update({ focus: shortFocus(day.workOn) })}>{t('Use the first sentence')}</button></>
                )}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="routine-footer">
        {isMorning ? (
          <>
            <button type="button" className="secondary" onClick={() => openPage('/app/reflection')}>
              {t('Done trading? End my day')}
            </button>
            <button type="button" className="start-day-button" onClick={() => openPage('/app/session')}>
              {t("I'm ready. Start my day")} &rarr;
            </button>
          </>
        ) : (
          <>
            <button type="button" className="secondary" onClick={() => openPage('/app/morning')}>&larr; {t('Back to morning prep')}</button>
            <button type="button" onClick={() => openPage('/app')}>{t('Done, back to my journal')}</button>
          </>
        )}
      </div>

      {editing && routine && (
        <RoutineEditor
          routine={routine}
          onSaved={(saved) => {
            setRoutine(saved);
            setEditing(false);
          }}
          onClose={() => setEditing(false)}
          onSessionExpired={onSessionExpired}
        />
      )}
    </div>
  );
}

export default Routine;
